import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Icon} from '@iconify/react';
import GeneratorActions, {GeneratorAction} from './GeneratorActions';
import GeneratorContent from './GeneratorContent';
import GeneratorPromptInput from './GeneratorPromptInput';
import {usePresentationPrototype} from './usePresentationPrototype';
import {useFileTransfer} from './useFileTransfer';
import GeneratorModals from './GeneratorModals';
import PageHeader from '../../components/common/header/PageHeader';
import Button from '../../../incase-domain/components/button/Button';
import i18n from '../../../i18n';
import {PresentationPrototypeType} from '../../../incase-domain/presentationPrototype';
import {prettyDateFormat} from '../../../utils/dateTimeUtils';
import {SlideSize} from './types';

function GeneratorPage(): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {isParsing, isPdfParsing, pdfProgress, pdfPages, pickAndParse, download, clearPdfPages} = useFileTransfer();

    const [prompt, setPrompt] = React.useState<string>('');
    const [type, setType] = React.useState<PresentationPrototypeType | undefined>(PresentationPrototypeType.MEDICAL_CASE);
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [confirmNewSessionOpen, setConfirmNewSessionOpen] = React.useState<boolean>(false);
    const [confirmRevertPromptOpen, setConfirmRevertPromptOpen] = React.useState<boolean>(false);
    const [revertPromptId, setRevertPromptId] = React.useState<string>('');
    const [insertSuccessModalOpen, setInsertSuccessModalOpen] = React.useState<boolean>(false);
    const [insertedPresentationId, setInsertedPresentationId] = React.useState<string>('');
    const [slideSize, setSlideSize] = React.useState<SlideSize | undefined>();

    const {
        prototype,
        setPrototype,
        promptHistory,
        error,
        isLoading: isMutating,
        generate,
        modify,
        update,
        organize,
        revert,
        insert,
        createNew,
    } = usePresentationPrototype({
        onInsertSuccess: (presentationId) => {
            setInsertedPresentationId(presentationId);
            setInsertSuccessModalOpen(true);
        },
    });

    const handleSubmit = (): void => {
        const clearPrompt = (): void => setPrompt('');
        if (prototype?._id && prototype.content) {
            modify(prompt, clearPrompt);
        } else if (prototype?._id) {
            generate({prompt, type, id: prototype._id}, clearPrompt);
        } else {
            generate({prompt, type}, clearPrompt);
        }
    };

    const handleDownload = (): void => {
        if (!prototype) return;
        download('presentation_prototype.md', prototype.content);
    };

    const handleUpload = (): void => {
        pickAndParse().then((result) => {
            if (!result) return;
            if (prototype?._id) {
                const updated = {...prototype, content: result.content};
                setPrototype(updated);
                update(updated);
            } else {
                setPrototype({_id: '', content: result.content});
            }
        });
    };

    const isLoading = isMutating || isParsing;

    const leftActions = [
        {
            type: 'button',
            label: isEditing ? t('generator.preview') : t('generator.edit'),
            icon: isEditing ? 'solar:notebook-minimalistic-linear' : 'solar:pen-2-linear',
            onClick: () => setIsEditing(!isEditing),
            disabled: isLoading || !prototype,
        },
        {
            type: 'dropdown',
            label: t('generator.history'),
            icon: 'solar:notebook-minimalistic-linear',
            disabled: isLoading,
            items: [
                promptHistory.map((item) => ({
                    label: item.prompt,
                    description: prettyDateFormat(new Date(item.created), i18n.language),
                    onClick: () => {
                        setRevertPromptId(item._id);
                        setConfirmRevertPromptOpen(true);
                    },
                })),
            ],
        },
        {
            type: 'select',
            label: t('generator.size'),
            disabled: isLoading,
            options: (['sm', 'md', 'lg', 'xl'] as const).map((item) => ({
                value: item,
                label: t(`generator.sizeTypes.${item}`),
            })),
            value: slideSize,
            onChange: (value) => setSlideSize(value as SlideSize | undefined),
            isClearable: true,
        },
        {
            type: 'button',
            label: t('generator.organizeBySlides'),
            icon: 'solar:layers-linear',
            onClick: () => organize({slideSize, pages: pdfPages, content: prototype?.content}),
            disabled: isLoading,
        },
    ] satisfies GeneratorAction[];

    const rightActions = [
        {
            type: 'button',
            label: t('common.save'),
            icon: 'solar:diskette-linear',
            onClick: () => update(undefined, () => setIsEditing(false)),
            disabled: isLoading || !prototype,
            hidden: !isEditing,
        },
        {
            type: 'button',
            label: t('generator.insertIntoNCC'),
            icon: 'solar:import-linear',
            onClick: () => insert(prototype?.content ?? ''),
            disabled: isLoading || !prototype,
        },
        {
            type: 'button',
            label: t('generator.download'),
            icon: 'solar:download-linear',
            onClick: () => handleDownload(),
            disabled: isLoading || !prototype,
        },
    ] satisfies GeneratorAction[];

    return (
        <>
            <div className="c-generatorPage l-container">
                <div className="c-generatorPage__header">
                    <PageHeader
                        title={t('navigation.generator')}
                        trailing={
                            <>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    color="secondary"
                                    leading="solar:pen-new-square-outline"
                                    onClick={() => setConfirmNewSessionOpen(true)}
                                    disabled={isLoading || !prototype?.content}
                                >
                                    {t('generator.new')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    color="secondary"
                                    leading="solar:upload-minimalistic-linear"
                                    onClick={() => handleUpload()}
                                    disabled={isLoading}
                                >
                                    {t('generator.upload')}
                                </Button>
                            </>
                        }
                    />
                </div>

                {!prototype && !isLoading && (
                    <div className="c-generatorPage__landing">
                        <Icon icon="healthicons:artificial-intelligence" />
                        <div className="c-generatorPage__landing_title">{t('generator.landingTitle')}</div>
                        <div className="c-generatorPage__landing_subtitle">{t('generator.landingSubtitle')}</div>
                    </div>
                )}

                {(prototype || isLoading) && (
                    <>
                        <GeneratorContent
                            prototype={prototype}
                            isEditing={isEditing}
                            onContentChange={(content) => prototype && setPrototype({...prototype, content})}
                            isLoading={isLoading}
                            isPdfParsing={isPdfParsing}
                            pdfProgress={pdfProgress}
                        />

                        <GeneratorActions leftActions={leftActions} rightActions={rightActions} />
                    </>
                )}

                <GeneratorPromptInput
                    prompt={prompt}
                    onPromptChange={setPrompt}
                    onSubmit={handleSubmit}
                    hasPrototype={!!prototype}
                    type={type}
                    onTypeChange={setType}
                    isLoading={isLoading}
                    error={error}
                />
            </div>

            <GeneratorModals
                newSession={{
                    isOpen: confirmNewSessionOpen,
                    onClose: () => setConfirmNewSessionOpen(false),
                    onConfirm: () => {
                        createNew(type);
                        setPrompt('');
                        setSlideSize(undefined);
                        clearPdfPages();
                        setConfirmNewSessionOpen(false);
                    },
                }}
                revertPrompt={{
                    isOpen: confirmRevertPromptOpen,
                    onClose: () => setConfirmRevertPromptOpen(false),
                    onConfirm: () => {
                        revert(revertPromptId);
                        setConfirmRevertPromptOpen(false);
                    },
                }}
                insertSuccess={{
                    isOpen: insertSuccessModalOpen,
                    onClose: () => setInsertSuccessModalOpen(false),
                    onViewPresentation: () => navigate(`/presentations/${insertedPresentationId}`),
                }}
            />
        </>
    );
}

export default GeneratorPage;
